import React, {forwardRef, useState} from 'react';
import {makeStyles} from "@mui/styles";
import {TextField as MuiTextField} from '@mui/material';
import {TextField} from "mui-rff";
import IconButton from "../button/icon_button";
import InputAdornment from "@mui/material/InputAdornment";
import {ReactComponent as EyeIcon} from "../../../../assets/images/password-icons/eye.svg";
import {ReactComponent as EyeInvisibleIcon} from "../../../../assets/images/password-icons/invisible-eye.svg";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '2px 8px',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        color: theme.vars.palette.background.paper,
    },
    input: {
        flex: 1,
        color: theme.vars.palette.background.paper,
    },
    innerInput: {
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&:focus': {
            backgroundColor: 'transparent',
        },
        [`& input`]: {
            borderRadius: theme.shape.borderRadius,
        },
    },
    divider: {
        height: 35,
        margin: 4,
        width: 1,
        color: theme.vars.palette.divider,
    },
    label: {
        backgroundColor: theme.vars.palette.background.paper,
        padding: '0 25px',
        transform: 'translate(0, 20px) scale(1)',
    },
    shrink: {
        backgroundColor: theme.vars.palette.background.paper,
        padding: '0 25px',
        transform: 'translate(0, -4px) scale(0.75)',
    }
}));

/**
 *
 * @param placeholder
 * @param label
 * @param ariaLabel
 * @param value
 * @param onChange
 * @param type
 * @param icon
 * @param onIconClick
 * @param fullWidth
 * @param iconButtonProps
 * @param {Omit<import('@mui/material').OutlinedTextFieldProps, 'variant'>} props
 * @return {JSX.Element}
 * @constructor
 */
export const OutlinedInput = ({
                                  placeholder = 'type...',
                                  label,
                                  ariaLabel = 'input',
                                  value,
                                  onChange,
                                  type = 'text',
                                  icon,
                                  onIconClick,
                                  fullWidth = true,
                                  iconButtonProps,
                                  ...props
                              }) => {
    return (
        <MuiTextField
            variant={'outlined'}
            fullWidth={fullWidth}
            placeholder={placeholder}
            label={label}
            type={type}
            value={value}
            onChange={(e) => onChange && onChange(e.target.value)}
            {...props}
            InputProps={{
                ...(props?.InputProps ?? {}),
                endAdornment: icon
                    ? (
                        <InputAdornment position="end">
                            <IconButton
                                onPressed={onIconClick}
                                onMouseDown={(e) => e.preventDefault()}
                                color={'paper.secondary'}
                                {...iconButtonProps}
                            >
                                {icon}
                            </IconButton>
                        </InputAdornment>
                    )
                    : props?.InputProps?.endAdornment,
            }}
        />
    );
}

/**
 * @param {import('mui-rff').TextFieldProps} props
 * @return {JSX.Element}
 * @constructor
 */
export const OutlinedInputForm = forwardRef(({
                                                 paddingLeft = '',
                                                 startAdornment,
                                                 placeholder = 'type...',
                                                 label,
                                                 ariaLabel = 'input',
                                                 type = 'text',
                                                 required = false,
                                                 fullWidth = true,
                                                 helperText,
                                                 name,
                                                 disabled,
                                                 className,
                                                 ...props
                                             }, ref) => {
    const classes = useStyles();
    const [show, setShow] = useState(false);

    const toggleShow = () => {
        setShow(prevState => !prevState);
    }

    return (
        <TextField
            style={{paddingLeft: paddingLeft}}
            inputRef={ref}
            disabled={disabled}
            helperText={helperText}
            fullWidth={fullWidth}
            required={required}
            type={(type !== 'password') ? type : (!show ? 'password' : 'text')}
            variant={'outlined'}
            label={label}
            name={name}
            classes={{root: classes.innerInput,}}
            className={`${classes.input} ${className}`}
            placeholder={placeholder}
            InputProps={
                {
                    style: {paddingLeft: paddingLeft},
                    startAdornment: startAdornment,
                    'aria-label': ariaLabel,
                    ...(type === 'password' ? {
                        endAdornment: (
                            <>
                                {
                                    !show
                                        ? <EyeIcon className={'cursor-pointer-hover'}
                                                   onClick={toggleShow}
                                        />
                                        : <EyeInvisibleIcon className={'cursor-pointer-hover'}
                                                            onClick={toggleShow}
                                        />
                                }
                            </>
                        )
                    } : {})
                }
            }
            {...props}
        />
    );
});

